import React, { memo, useCallback } from 'react';
import { classNames } from '../../utils/classNames';
import {
  getFileTypeInfo,
  getAttachmentName
} from '../../utils/attachmentUtils';
import { CloseIcon } from '../Icons';
import type { AttachmentDisplayItem } from '../../types/attachments';

export interface AttachmentPreviewRowProps {
  item: AttachmentDisplayItem;
  index: number;
  onRemove: (id: string) => void;
  onFileClick?: (index: number) => void;
}

/** Always show type/slug badge in the preview row for consistency across Add Comment, Reply, and Edit. */
function AttachmentPreviewRowComponent({
  item,
  index,
  onRemove,
  onFileClick
}: AttachmentPreviewRowProps): React.ReactElement {
  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(item.id);
    },
    [item.id, onRemove]
  );
  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-remove]')) return;
      onFileClick?.(index);
    },
    [index, onFileClick]
  );

  const { label, slug } = getFileTypeInfo(item);
  const name = getAttachmentName(item);

  return (
    <div
      className={classNames(
        'attachment-preview-popover__row',
        onFileClick && 'attachment-preview-popover__row--clickable'
      )}
      role={onFileClick ? 'button' : undefined}
      onClick={onFileClick ? handleRowClick : undefined}
      onKeyDown={
        onFileClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onFileClick(index);
              }
            }
          : undefined
      }
      tabIndex={onFileClick ? 0 : undefined}
    >
      <div className="attachment-preview-popover__row-preview">
        <span
          className={classNames(
            'attachment-preview-popover__file-type',
            `attachment-preview-popover__file-type--${slug}`
          )}
          aria-hidden
        >
          {label}
        </span>
      </div>
      <span className="attachment-preview-popover__row-name" title={name}>
        {name}
      </span>
      <button
        type="button"
        className="attachment-preview-popover__remove"
        onClick={handleRemove}
        data-remove
        aria-label={`Remove ${name}`}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export const AttachmentPreviewRow = memo(AttachmentPreviewRowComponent);
AttachmentPreviewRow.displayName = 'AttachmentPreviewRow';
